import axios from "../axios";

// Export reports as excel. Override format to xlsx
export async function fetchReportExcel(payload) {
  return axios
    .get(`/service/reports/`, {
      params: payload,
      responseType: "blob",
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchPublicReport(token) {
  return axios.get(`/service/reports/public/${token}`);
}

export async function fetchReports(controller) {
  return axios({ method: "get", url: "/service/reports", params: controller })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchReportGoogleMapsRoute(controller) {
  return axios({
    method: "get",
    url: "/service/reports/googleMapsRoute",
    params: controller,
  })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response || error);
    });
}

export async function fetchReport(id, params = {}) {
  return axios.get(`/service/reports/` + id, { params: params });
}

/** Financial summary (costs/income/profit) for a service report — computed server-side; not part of `fetchReport` default fields. */
export async function fetchReportSummary(reportId) {
  return axios.get(`/service/reports/${reportId}/summary`);
}


export async function fetchReportItems(reportId, params = {}) {
  return axios.get(`/service/reports/${reportId}/items`, { params: params });
}

export async function fetchReportItem(reportId, itemId, params = {}) {
  return axios.get(`/service/reports/${reportId}/items/${itemId}`, { params: params });
}

export async function checkReportNumber(payload) {
  return axios.get(`/service/reports/checkNumber`, {
    params: payload,
  });
}

export async function nextReportNumber() {
  return axios.get(`/service/reports/nextNumber`);
}

export async function createReport(payload) {
  return axios
    .post(`/service/reports`, { report: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function createReportChecklist(payload) {
  return axios.post(
    `/service/reports/${payload.report.id}/createChecklist`,
    payload
  );
}

export async function updateReport(payload) {
  return axios
    .put(`/service/reports/${payload.id}`, { report: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

/** Only report crew — small JSON in/out vs full report update. */
export async function updateReportParticipants(reportId, participants) {
  return axios
    .put(`/service/reports/${reportId}/participants`, { participants })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function addReportItem(payload) {
  return axios
    .put(`/service/reports/${payload.id}/add_item`, { report: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function updateReportItem(reportId, item) {
  if (!item || !item.id) {
    throw new Error("Item ID is required");
  }
  
  return axios
    .put(`/service/reports/${reportId}/items/${item.id}`, { 
      item: {
        quantity: item.quantity,
        description: item.description,
        unit_id: item.unit_id,
        sales_price: item.sales_price,
        purchase_price: item.purchase_price,
        discount: item.discount,
        surcharge: item.surcharge,
        product_id: item.product_id,
        taken_in_vehicle: item.taken_in_vehicle || false,
        not_used: item.not_used || false,
        quantity_used: item.quantity_used || null,
        quantity_not_used: item.quantity_not_used || null,
        returned_to_storage: item.returned_to_storage || false,
        left_in_vehicle: item.left_in_vehicle || false,
        storage_id: item.storage_id || null,
        picked: item.picked || false,
        picked_at: item.picked_at || null,
      }
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error in updateReportItem:", error);
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(", "));
      }
      throw error;
    });
}

export async function deleteReportItem(reportId, itemId) {
  if (!itemId) {
    throw new Error("Item ID is required");
  }
  
  return axios
    .delete(`/service/reports/${reportId}/items/${itemId}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Error in deleteReportItem:", error);
      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors.join(", "));
      }
      throw error;
    });
}

/** Creates a `ReportPriceLine` on the service report (agreed price line). */
export async function createReportPriceLine(reportId, reportPriceLine) {
  return axios
    .post(`/service/reports/${reportId}/price_lines`, {
      report_price_line: reportPriceLine,
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function copyReport(payload) {
  return axios.put(`/service/reports/${payload.id}/copy`, {
    report: payload,
  });
}

export async function updateReportAgreement(payload) {
  return axios.put(`/service/reports/${payload.id}/updateAgreement`, {
    report: payload,
  });
}

export async function cancelReportAgreement(payload) {
  return axios.put(`/service/reports/${payload.id}/cancelAgreement`, {
    report: payload,
  });
}

export async function printReport(payload) {
  return axios.put(`/service/reports/${payload.id}/print`, {
    report: payload,
    params: {
      printReportType: payload.printReportType,
      include_images: payload.include_images,
    },
  });
}

export async function sendAgreementConfirmationReminder(payload) {
  return axios.put(
    `/service/reports/${payload.id}/sendAgreementConfirmationReminder`,
    {
      ...payload,
    }
  );
}

export async function resendReportAppointment(payload) {
  return axios.put(`/service/reports/${payload.id}/resendAppointment`, {
    ...payload,
  });
}

export async function sendUpdatedAppointmentToCustomer(payload) {
  return axios.put(
    `/service/reports/${payload.id}/send_updated_appointment_to_customer`,
    {
      ...payload,
    }
  );
}

export async function excelReports(payload) {
  const response = await axios.get(`/service/reports/excel`, {
    params: payload,
    responseType: "blob",
    headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json" },
  });
  const blob = response.data;
  if (typeof Blob !== "undefined" && blob instanceof Blob) {
    const sniff = await readBlobAsText(blob.slice ? blob.slice(0, 5) : blob);
    if (sniff.startsWith("PK")) {
      const excelUrl =
        typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(blob)
          : "";
      return { data: { excel_url: excelUrl }, excel_url: excelUrl };
    }
    const text = await readBlobAsText(blob);
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json.errors) && json.errors.length) {
        throw new Error(json.errors.join(", "));
      }
      return { data: json, ...json };
    } catch (error) {
      if (error instanceof SyntaxError) {
        const err = new Error("Invalid excel");
        err.code = "REPORTS_EXCEL_INVALID";
        throw err;
      }
      throw error;
    }
  }
  return response;
}

export async function exportReportsPdf(payload) {
  try {
    const response = await axios.get(`/service/reports/export_pdf`, {
      params: payload,
      responseType: "blob",
    });
    
    // Check if response is actually a PDF blob
    if (response.data instanceof Blob) {
      // Verify it's a PDF by checking the first bytes
      // Create a clone to avoid consuming the original blob
      const clonedBlob = response.data.slice(0);
      const arrayBuffer = await clonedBlob.slice(0, 4).arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const firstBytes = String.fromCharCode(...uint8Array);
      
      if (!firstBytes.startsWith('%PDF')) {
        // Not a PDF, might be JSON error - read the whole blob
        const text = await clonedBlob.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json.errors?.join(', ') || json.error || 'Ukjent feil');
        } catch (e) {
          if (e.message.includes('errors') || e.message.includes('error')) {
            throw e;
          }
          throw new Error('Mottok ugyldig respons fra server');
        }
      }
    }
    
    return response;
  } catch (error) {
    // If error response has data, try to parse as JSON
    if (error.response && error.response.data) {
      // If it's a blob, try to read it as text to check if it's JSON
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          throw new Error(json.errors?.join(', ') || json.error || 'Ukjent feil');
        } catch (e) {
          // Not JSON, rethrow original error
          throw error;
        }
      }
    }
    throw error;
  }
}

export async function getPdfReport(payload) {
  const response = await axios.get(`/service/reports/${payload.id}/pdf`, {
    params: payload.params,
    responseType: "blob",
    headers: { Accept: "application/pdf, application/zip, application/json" },
  });
  const blob = response.data;
  if (typeof Blob !== "undefined" && blob instanceof Blob) {
    const sniff = await readBlobAsText(blob.slice ? blob.slice(0, 5) : blob);
    if (sniff.startsWith("%PDF") || sniff.startsWith("PK")) {
      const pdfUrl =
        typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(blob)
          : "";
      const payloadOut = {
        pdf_url: pdfUrl,
        type: sniff.startsWith("PK") ? "zip" : "pdf",
      };
      return { data: payloadOut, ...payloadOut };
    }
    const text = await readBlobAsText(blob);
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json.errors) && json.errors.length) {
        throw new Error(json.errors.join(", "));
      }
      return { data: json, ...json };
    } catch (error) {
      if (error instanceof SyntaxError) {
        const err = new Error("Invalid pdf");
        err.code = "REPORTS_PDF_INVALID";
        throw err;
      }
      throw error;
    }
  }
  return response;
}

function readBlobAsText(blob) {
  if (typeof blob?.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

export async function sendReport(payload) {
  return axios.put(`/service/reports/${payload.id}/send`, {
    report: payload,
  });
}

export async function checkSendReportStatus(jobId) {
  return axios.get(`/service/reports/send/status/${jobId}`);
}

export async function checkPdfStatus(jobId) {
  return axios.get(`/service/reports/pdf/status/${jobId}`);
}

export async function fetchReportOrderPreview(payload) {
  return axios.get(`/service/reports/${payload.id}/preview_order`, {
    report: payload,
  });
}

export async function previewReportAgreementMessage(payload) {
  return axios.put(`/service/reports/${payload.id}/previewAgreementMessage`, {
    report: payload,
  });
}

export async function newReportAgreement(payload) {
  return axios.put(`/service/reports/${payload.id}/newAgreement`, {
    report: payload,
  });
}

export async function deleteReport(payload) {
  const report = payload.report;
  return axios
    .delete(`/service/reports/${report.report_id}`, {
      data: JSON.stringify({
        report,
      }),
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function deleteReportChecklist(payload) {
  return axios
    .delete(`/service/reports/${payload.id}/deleteChecklist`, payload)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function listUpdateReport(payload) {
  return axios
    .put(`/service/reports/list`, { ids: payload.ids, report: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchReportPriceEstimate(payload) {
  return axios.get(`/service/reports/${payload.id}/price_estimate`, {
    params: payload,
  });
}

export async function generateInvoiceDescription(payload) {
  return axios.post(`/service/reports/${payload.id}/generate_invoice_description`, {
    report: payload,
  });
}

export async function generateReportFormatting(payload) {
  return axios.post(`/service/reports/${payload.id}/generate_report_formatting`, {
    report: payload,
  });
}

export async function analyzeReport(reportId) {
  return axios.post(`/service/reports/${reportId}/analyze`);
}

export async function reviewReportAnalysis(reportId, payload) {
  return axios.post(`/service/reports/${reportId}/analysis_review`, {
    report: payload,
  });
}

export async function createOrderFromAnalysis(reportId) {
  return axios.post(`/service/reports/${reportId}/create_order_from_analysis`);
}

export async function fetchReportChecklist(reportId, params = {}) {
  return axios
    .get(`/service/reports/${reportId}/checklist`, { params: params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      const data = error.response?.data;
      const status = error.response?.status ?? error.status;
      const errors = data?.errors;
      const benignMissing =
        status === 404 ||
        (Array.isArray(errors) &&
          errors.some(
            (err) =>
              typeof err === "string" &&
              (err.includes("does not have a checklist") || err.includes("404"))
          ));
      // Soft envelope: missing checklist is not a hard failure for UI loaders.
      if (benignMissing) {
        return {
          errors: Array.isArray(errors)
            ? errors
            : ["Report does not have a checklist"],
          missing: true,
        };
      }
      return Promise.reject(data || error);
    });
}

export async function updateReportChecklist(reportId, payload) {
  return axios
    .put(`/service/reports/${reportId}/update_checklist`, { report: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      const data = error?.response?.data;
      const errors = data?.errors || [
        data?.error || error?.message || "Kunne ikke lagre sjekkliste",
      ];
      return Promise.reject({
        ...data,
        status: error?.response?.status,
        errors: Array.isArray(errors) ? errors : [String(errors)],
      });
    });
}